// Account Overview Component
import React, { useState, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { fetchCrmEntities, fetchTenantUsers, fetchAssociations, fetchContactEntries, fetchActivities, fetchHospitality, fetchMarketing, fetchHorses } from '../../lib/queryFunctions';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Skeleton } from '../ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
} from '../../components/ui/collapsible';
import { 
  Star, 
  CheckCircle2,
  Pencil,
  Link2,
} from 'lucide-react';
import { associationAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useConfirm } from '../../components/common/ConfirmDialog';
import { SectionHeader, TaskStatusIndicator } from '../common/OverviewHelpers'; // eslint-disable-line no-unused-vars
import AccountAssociationsCard from './overview/AccountAssociationsCard';
import AccountFilesSection    from './overview/AccountFilesSection';
import AccountTasksSection    from './overview/AccountTasksSection';
import AccountCommsSection    from './overview/AccountCommsSection';
import AccountHospitalitySection from './overview/AccountHospitalitySection';
import AccountMarketingSection   from './overview/AccountMarketingSection';
import AccountDetailsCard    from './overview/AccountDetailsCard';
import AccountNotesCard      from './overview/AccountNotesCard';

// Rating colors - consistent with All Accounts page
const ratingColors = {
  A: 'bg-green-100 text-green-800',
  B: 'bg-blue-100 text-blue-800',
  C: 'bg-yellow-100 text-yellow-800',
  D: 'bg-orange-100 text-orange-800',
  F: 'bg-red-100 text-red-800',
  unrated: 'bg-gray-100 text-gray-600',
};

// SectionHeader and TaskStatusIndicator imported from OverviewHelpers above

const AccountOverview = ({ account, loading, onAccountUpdate, canEditAccount }) => {
  const navigate = useNavigate();
  const confirm = useConfirm(); // used by association dialog
  const qc = useQueryClient();
  const accountId = account?.id;

  // Association dialog state
  const [assocDialogOpen,    setAssocDialogOpen]    = useState(false);
  const [selectedAssociation, setSelectedAssociation] = useState(null);
  const [assocForm,          setAssocForm]          = useState({});
  const [isEditingAssoc,     setIsEditingAssoc]     = useState(false);
  const [savingAssoc,        setSavingAssoc]        = useState(false);

  // ── Server state via useQuery ──────────────────────────────────────────────
  const TASK_PARAMS = useMemo(() => ({ crm_entity_id: accountId, activity_type: 'task' }), [accountId]);
  const COMM_PARAMS = useMemo(() => ({ crm_entity_id: accountId, activity_type: 'communication' }), [accountId]);

  const { data: allEntities = [] } = useQuery({
    queryKey: QK.crmEntities(),
    queryFn: fetchCrmEntities(),
    enabled: !!accountId,
  });
  // Build entity map for association name lookups
  const entityMap = useMemo(() => Object.fromEntries(allEntities.map(e => [e.id, e])), [allEntities]);

  const { data: rawAssociations = [] } = useQuery({
    queryKey: QK.associations(accountId),
    queryFn: fetchAssociations(accountId),
    enabled: !!accountId,
  });
  const keyAssociations = useMemo(() =>
    rawAssociations.filter(a => a.is_key_association && !a.status?.startsWith('past')),
  [rawAssociations]);
  const allAssociations = useMemo(() =>
    rawAssociations.map(a => {
      const otherId = a.crm_entity_1_id === accountId ? a.crm_entity_2_id : a.crm_entity_1_id;
      return { ...a, entityName: entityMap[otherId]?.system_name || 'Unknown' };
    }),
  [rawAssociations, entityMap, accountId]);

  const { data: usersData = [] } = useQuery({
    queryKey: QK.tenantUsers(),
    queryFn: fetchTenantUsers(),
    enabled: !!accountId,
  });
  const usersMap = useMemo(() => Object.fromEntries(
    usersData.map(u => [u.id, u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email])
  ), [usersData]);
  const users = useMemo(() => usersData.map(u => ({ id: u.id, name: usersMap[u.id] })), [usersData, usersMap]);

  const { data: allTasksData = [], isLoading: tasksLoading } = useQuery({
    queryKey: QK.activities(TASK_PARAMS),
    queryFn: fetchActivities(TASK_PARAMS),
    enabled: !!accountId,
  });
  const tasks = useMemo(() =>
    allTasksData
      .filter(t => t.task_status !== 'completed' && t.task_status !== 'cancelled')
      .sort((a, b) => {
        if (!a.end_date_time && !b.end_date_time) return 0;
        if (!a.end_date_time) return 1;
        if (!b.end_date_time) return -1;
        return new Date(a.end_date_time) - new Date(b.end_date_time);
      }),
  [allTasksData]);
  const allTasks = allTasksData;

  const { data: allCommsData = [], isLoading: commsLoading } = useQuery({
    queryKey: QK.activities(COMM_PARAMS),
    queryFn: fetchActivities(COMM_PARAMS),
    enabled: !!accountId,
  });
  const allComms = useMemo(() =>
    [...allCommsData].sort((a, b) => {
      const da = a.start_date_time ? new Date(a.start_date_time) : new Date(a.created_at);
      const db = b.start_date_time ? new Date(b.start_date_time) : new Date(b.created_at);
      return db - da;
    }),
  [allCommsData]);
  const comms = useMemo(() => allComms.slice(0, 10), [allComms]);

  const { data: hospitalityData = [], isLoading: hospLoading } = useQuery({
    queryKey: QK.hospitality({ crm_entity_id: accountId }),
    queryFn: fetchHospitality({ crm_entity_id: accountId }),
    enabled: !!accountId,
  });
  const hospitality = useMemo(() =>
    [...hospitalityData].sort((a, b) => new Date(b.date) - new Date(a.date)),
  [hospitalityData]);

  const { data: marketing = [], isLoading: mktLoading } = useQuery({
    queryKey: QK.marketing({ crm_entity_id: accountId }),
    queryFn: fetchMarketing({ crm_entity_id: accountId }),
    enabled: !!accountId,
  });

  const { data: horsesData = [] } = useQuery({
    queryKey: QK.horses({ include_archived: true }),
    queryFn: fetchHorses({ include_archived: true }),
    enabled: !!accountId,
  });
  const horses = horsesData;

  const { data: contactEntries = [] } = useQuery({
    queryKey: QK.contactEntries(accountId),
    queryFn: fetchContactEntries(accountId),
    enabled: !!accountId,
  });

  // Helper: look up entity name by id
  const getAssocName = (entityId) => entityMap[entityId]?.system_name || 'Unknown';

  // Invalidation helpers (targeted — only refresh the changed resource)
  const invalidateTasks         = () => qc.invalidateQueries({ queryKey: QK.activities(TASK_PARAMS), refetchType: 'all' });
  const invalidateComms         = () => qc.invalidateQueries({ queryKey: QK.activities(COMM_PARAMS), refetchType: 'all' });
  const invalidateHospitality   = () => qc.invalidateQueries({ queryKey: QK.hospitality({ crm_entity_id: accountId }), refetchType: 'all' });
  const invalidateMarketing     = () => qc.invalidateQueries({ queryKey: QK.marketing({ crm_entity_id: accountId }) });
  const invalidateAssociations  = () => qc.invalidateQueries({ queryKey: QK.associations(accountId) });
  const invalidateContactEntries = () => qc.invalidateQueries({ queryKey: QK.contactEntries(accountId) });

  // Association dialog handlers
  const openAssociationDialog = (assoc) => {
    setSelectedAssociation(assoc);
    setAssocForm({
      relationship: assoc.relationship || '',
      status: assoc.status || 'possible',
      is_key_association: assoc.is_key_association || false,
      job_title: assoc.job_title || '',
      notes: assoc.notes || '',
    });
    setIsEditingAssoc(false);
    setAssocDialogOpen(true);
  };

  const handleSaveAssociation = async () => {
    if (!selectedAssociation?.id) return;
    setSavingAssoc(true);
    try {
      await associationAPI.update(selectedAssociation.id, assocForm);
      toast.success('Association updated');
      
      // Update the selectedAssociation with the new values
      setSelectedAssociation({
        ...selectedAssociation,
        relationship: assocForm.relationship,
        status: assocForm.status,
        is_key_association: assocForm.is_key_association,
        job_title: assocForm.job_title,
        notes: assocForm.notes,
      });
      
      setIsEditingAssoc(false);
      // Refresh associations list
      invalidateAssociations();
    } catch (error) {
      console.error('Failed to save association:', error);
      toast.error('Failed to save association');
    } finally {
      setSavingAssoc(false);
    }
  };

  const cancelAssocEdit = () => {
    if (selectedAssociation) {
      setAssocForm({
        relationship: selectedAssociation.relationship || '',
        status: selectedAssociation.status || 'possible',
        is_key_association: selectedAssociation.is_key_association || false,
        job_title: selectedAssociation.job_title || '',
        notes: selectedAssociation.notes || '',
      });
    }
    setIsEditingAssoc(false);
  };

  if (loading || !account) return <Skeleton className="h-96" />;

  /** Inline skeleton rows shown while a section's query is still loading. */
  const SectionSkeleton = ({ rows = 3 }) => (
    <div className="space-y-2 mt-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 p-2">
          <Skeleton className="h-4 w-4 rounded-full shrink-0" />
          <Skeleton className="h-4 flex-1 rounded" />
          <Skeleton className="h-4 w-16 rounded" />
        </div>
      ))}
    </div>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="account-overview">
      {/* LEFT COLUMN */}
      <div className="space-y-6">
        
        {/* 1. Details — extracted to AccountDetailsCard */}
        <AccountDetailsCard
          account={account}
          canEditAccount={canEditAccount}
          onAccountUpdate={onAccountUpdate}
          users={users}
          usersMap={usersMap}
        />

        {/* 2. Key Associations — extracted to sub-component */}
        <AccountAssociationsCard
          account={account}
          keyAssociations={keyAssociations}
          entityMap={entityMap}
          onAssociationClick={openAssociationDialog}
        />

        {/* 3. Photos & Files — extracted to sub-component */}
        <AccountFilesSection
          account={account}
          onAccountUpdate={onAccountUpdate}
        />

        {/* 4. Notes — extracted to AccountNotesCard */}
        <AccountNotesCard
          account={account}
          canEditAccount={canEditAccount}
          onAccountUpdate={onAccountUpdate}
        />

      </div>

      {/* RIGHT COLUMN */}
      <div className="space-y-6">
        
        {/* Tasks — extracted to AccountTasksSection */}
        <AccountTasksSection
          account={account}
          canEditAccount={canEditAccount}
          tasks={tasks}
          allTasks={allTasks}
          tasksLoading={tasksLoading}
          invalidateTasks={invalidateTasks}
          users={users}
          usersMap={usersMap}
          allAssociations={allAssociations}
          allComms={allComms}
          contactEntries={contactEntries}
          horses={horses}
        />

        {/* Communications — extracted to AccountCommsSection */}
        <AccountCommsSection
          account={account}
          canEditAccount={canEditAccount}
          comms={comms}
          allComms={allComms}
          commsLoading={commsLoading}
          invalidateComms={invalidateComms}
          users={users}
          usersMap={usersMap}
          allAssociations={allAssociations}
          allTasks={allTasks}
          contactEntries={contactEntries}
          horses={horses}
        />

        {/* Hospitality — extracted to AccountHospitalitySection */}
        <AccountHospitalitySection
          account={account}
          canEditAccount={canEditAccount}
          hospitality={hospitality}
          hospLoading={hospLoading}
          invalidateHospitality={invalidateHospitality}
          allAssociations={allAssociations}
          usersMap={usersMap}
        />

        {/* Marketing — extracted to AccountMarketingSection */}
        <AccountMarketingSection
          account={account}
          canEditAccount={canEditAccount}
          marketing={marketing}
          mktLoading={mktLoading}
          invalidateMarketing={invalidateMarketing}
          contactEntries={contactEntries}
          usersMap={usersMap}
        />

      </div>


      {/* Association Detail Dialog */}
      <Dialog open={assocDialogOpen} onOpenChange={(open) => {
        setAssocDialogOpen(open);
        if (!open) {
          setIsEditingAssoc(false);
          setSelectedAssociation(null);
        }
      }}>
        <DialogContent data-testid="association-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5" />
              Association Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedAssociation && (
            <div className="space-y-4 py-2">
              {/* Linked Entities - Always read-only */}
              <div className="p-4 bg-muted/30 rounded">
                <div className="flex items-center justify-center gap-3">
                  <span 
                    className="text-sm font-medium text-primary hover:underline cursor-pointer"
                    onClick={async () => {
                      setAssocDialogOpen(false);
                    }}
                  >
                    {account.system_name}{account.additional_identifier ? ` (${account.additional_identifier})` : ''}
                  </span>
                  <Link2 className="w-4 h-4 text-muted-foreground" />
                  <span 
                    className="text-sm font-medium text-primary hover:underline cursor-pointer"
                    onClick={async () => {
                      const otherId = selectedAssociation.crm_entity_1_id === account.id 
                        ? selectedAssociation.crm_entity_2_id 
                        : selectedAssociation.crm_entity_1_id;
                      setAssocDialogOpen(false);
                      navigate(`/crm/accounts/${otherId}`);
                    }}
                  >
                    {getAssocName(
                      selectedAssociation.crm_entity_1_id === account.id 
                        ? selectedAssociation.crm_entity_2_id 
                        : selectedAssociation.crm_entity_1_id
                    )}
                  </span>
                </div>
              </div>

              {/* Editable Fields */}
              {isEditingAssoc ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Relationship</Label>
                    <Select value={assocForm.relationship} onValueChange={(val) => setAssocForm({...assocForm, relationship: val})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select relationship" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="shareholder_company">Shareholder/Company</SelectItem>
                        <SelectItem value="partnership">Partnership</SelectItem>
                        <SelectItem value="employee">Employee</SelectItem>
                        <SelectItem value="agent">Agent</SelectItem>
                        <SelectItem value="trainer">Trainer</SelectItem>
                        <SelectItem value="veterinarian">Veterinarian</SelectItem>
                        <SelectItem value="boarding">Boarding</SelectItem>
                        <SelectItem value="family_close">Family - Close</SelectItem>
                        <SelectItem value="family_extended">Family - Extended</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={assocForm.status} onValueChange={(val) => setAssocForm({...assocForm, status: val})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="current_possible">Current - Possible</SelectItem>
                        <SelectItem value="current_confirmed">Current - Confirmed</SelectItem>
                        <SelectItem value="past_possible">Past - Possible</SelectItem>
                        <SelectItem value="past_confirmed">Past - Confirmed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Job Title</Label>
                    <Input 
                      value={assocForm.job_title} 
                      onChange={(e) => setAssocForm({...assocForm, job_title: e.target.value})}
                      placeholder="Job title or role"
                    />
                  </div>

                  <div className="flex items-center gap-2 py-2">
                    <Button
                      type="button"
                      variant={assocForm.is_key_association ? "default" : "outline"}
                      size="sm"
                      onClick={() => setAssocForm({...assocForm, is_key_association: !assocForm.is_key_association})}
                      className="flex items-center gap-2"
                    >
                      <Star className={`w-4 h-4 ${assocForm.is_key_association ? 'fill-current' : ''}`} />
                      Key Association
                    </Button>
                  </div>

                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea 
                      value={assocForm.notes} 
                      onChange={(e) => setAssocForm({...assocForm, notes: e.target.value})}
                      placeholder="Add notes about this association..."
                      rows={3}
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm text-muted-foreground">Relationship</span>
                    <Badge variant="secondary" className="capitalize">{selectedAssociation.relationship?.replace('_', ' ') || '-'}</Badge>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm text-muted-foreground">Status</span>
                    <Badge variant={selectedAssociation.status?.includes('confirmed') ? 'default' : 'outline'} className="capitalize">
                      {selectedAssociation.status?.replace('_', ' ') || 'Unknown'}
                    </Badge>
                  </div>
                  {selectedAssociation.job_title && (
                    <div className="flex items-center justify-between py-2 border-b">
                      <span className="text-sm text-muted-foreground">Job Title</span>
                      <span className="text-sm">{selectedAssociation.job_title}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-sm text-muted-foreground">Key Association</span>
                    <Star className={`w-4 h-4 ${selectedAssociation.is_key_association ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                  </div>
                  {selectedAssociation.notes && (
                    <div className="py-2">
                      <span className="text-sm text-muted-foreground block mb-1">Notes</span>
                      <p className="text-sm">{selectedAssociation.notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <div className="flex justify-between w-full items-center">
              {canEditAccount && !isEditingAssoc && (
                <Button variant="ghost" size="icon" onClick={() => setIsEditingAssoc(true)} data-testid="edit-assoc-btn">
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
              {isEditingAssoc ? (
                <div className="flex gap-2 ml-auto">
                  <Button variant="outline" onClick={cancelAssocEdit} disabled={savingAssoc}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveAssociation} disabled={savingAssoc}>
                    {savingAssoc ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              ) : (
                <Button variant="outline" onClick={() => setAssocDialogOpen(false)} className="ml-auto">
                  Close
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
    </div>
  );
};

export default AccountOverview;
